import React, { useEffect, useState } from "react";
import { Button, Header, Icon, Table, Pagination, Segment, Label, Grid, Input, Modal } from "semantic-ui-react";
import { toast } from "react-toastify";
import { JustificationDto } from "../../../models/requests/justification-dto";
import useJustificationService from "../../../services/backoffice/hooks/useJustificationService";
import { ToasterConfig } from "../../../config/ToasterConfig";
import TooltipInfo from "../../tooltip/tooltip";
import { SessionUser } from "../../../models/session-user";
import SessionStorage from "../../../store/session";
import { useNavigate } from "react-router-dom";
import JustificationForm from "./create-justification-form";
import { JustificationFilters } from "../../../models/requests/justification-filters";
import { getJustificationTypeName } from "../../../utils/enum-helpers";

interface JustificationsListProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
}

const JustificationsList: React.FC<JustificationsListProps> = ({ isModal = false, open = false, onClose }) => {
    const [justifications, setJustifications] = useState<JustificationDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<JustificationFilters>({ Page: 1 });
    const [totalPages, setTotalPages] = useState<number>(0);
    const { getJustifications, deleteJustification, activateJustification, deactivateJustification } = useJustificationService();
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [editJustificationId, setEditJustificationId] = useState<string | null>(null);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }
        updateSessionUser();
    }, []);

    useEffect(() => {
        async function loadJustifications() {
            setLoading(true);
            try {
                const response = await getJustifications(filter);
                setJustifications(response.itens);
                setTotalPages(response.totalPages);
            } catch (err: any) {
                setError("Erro ao carregar justificativas.");
            } finally {
                setLoading(false);
            }
        }
        loadJustifications();
    }, [filter]);

    const handleDelete = async (id: string) => {
        try {
            const result = window.confirm("Tem certeza que deseja excluir esta justificativa?");
            if (result) {
                await deleteJustification(id);
                toast.success("Justificativa excluída com sucesso", ToasterConfig.success);
                setJustifications(justifications.filter((j) => j.id !== id));
            }
        } catch (err: any) {
            toast.error("Erro ao excluir justificativa", ToasterConfig.error);
        }
    };

    const handleActivation = async (justification: JustificationDto) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${justification.isActive ? 'desativar' : 'ativar'} esta justificativa?`);
            if (result) {
                if (justification.isActive) {
                    await deactivateJustification(justification.id);
                } else {
                    await activateJustification(justification.id);
                }
                setFilter({ ...filter }); // Atualiza a lista
                toast.success(`Justificativa ${justification.isActive ? 'desativada' : 'ativada'} com sucesso.`, ToasterConfig.success);
            }
        } catch (err: any) {
            toast.error("Erro ao alterar status da justificativa", ToasterConfig.error);
        }
    };

    const handleEdit = (id: string) => {
        setEditJustificationId(id);
        setModalOpen(true);
    };

    const formContent = (
        <Segment raised style={{ maxWidth: "900px", margin: "5px auto", padding: "20px" }}>
            <Header as="h3" textAlign="center" color={'green'}>
                Justificativas
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {sessionUser?.userData?.profile === 'Admin' && (
                        <Button color="green" onClick={() => setModalOpen(true)}>
                            <Icon name='plus' /> Nova Justificativa
                        </Button>
                    )}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color="green">
                        <Icon name={showFilters ? 'hide' : 'unhide'} />
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>

            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={6}>
                                <label htmlFor="code">Código</label>
                                <Input
                                    fluid
                                    id="code"
                                    icon="search"
                                    type="text"
                                    placeholder="Código da justificativa"
                                    value={filter?.code ?? ''}
                                    onChange={(event) => setFilter({ ...filter, code: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={6}>
                                <label htmlFor="description">Descrição</label>
                                <Input
                                    fluid
                                    id="description"
                                    icon="search"
                                    type="text"
                                    placeholder="Descrição da justificativa"
                                    value={filter?.description ?? ''}
                                    onChange={(event) => setFilter({ ...filter, description: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color="green" onClick={() => setFilter({ ...filter, Page: 1 })}>Buscar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}

            <Table color="green">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Código</Table.HeaderCell>
                        <Table.HeaderCell>Descrição</Table.HeaderCell>
                        <Table.HeaderCell>Tipo</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {justifications?.map((justification) => (
                        <Table.Row key={justification.id}>
                            <Table.Cell>
                                <Button.Group>
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Editar justificativa"
                                            trigger={
                                                <Button
                                                    color="green"
                                                    onClick={() => handleEdit(justification.id)}
                                                    icon="edit"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Excluir justificativa"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => handleDelete(justification.id)}
                                                    icon="trash"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content={justification.isActive ? "Desativar justificativa" : "Ativar justificativa"}
                                            trigger={
                                                <Button
                                                    color={justification.isActive ? 'red' : 'green'}
                                                    onClick={() => handleActivation(justification)}
                                                    icon={justification.isActive ? 'power off' : 'power'}
                                                />
                                            }
                                        />
                                    )}
                                </Button.Group>
                            </Table.Cell>
                            <Table.Cell>{justification.code}</Table.Cell>
                            <Table.Cell>{justification.description}</Table.Cell>
                            <Table.Cell>{getJustificationTypeName(justification.type)}</Table.Cell>
                            <Table.Cell>
                                <Label color={justification.isActive ? 'green' : 'red'}>
                                    {justification.isActive ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            <Pagination
                totalPages={totalPages}
                defaultActivePage={filter?.Page ?? 1}
                onPageChange={(e, { activePage }) => setFilter({ ...filter, Page: activePage as number })}
            />
        </Segment>
    );

    return (
        <>
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                size="tiny"
                style={{ width: '680px', maxWidth: '95vw', padding: '20px' }}
            >
                <JustificationForm
                    isModal
                    open={modalOpen}
                    justificationId={editJustificationId || undefined}
                    onClose={() => {
                        setModalOpen(false);
                        setEditJustificationId(null);
                        setFilter({ ...filter });
                    }}
                />
            </Modal>

            {formContent}
        </>
    );
};

export default JustificationsList;
